import React from 'react'

import { Box, ModalBody, ModalClose, ModalFooter, ModalHeader } from '@gousto-internal/citrus-react'

import type { MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import type { RecipeCoreId, RecipeUUID } from 'reducers/menu-recipes/recipes'
import { SectionPanel } from 'routes/Menu/components/Detail/SectionPanel'

import { AddRecipeButton } from './AddRecipeButton'
import { RecipeAllergens } from './Allergens'
import { RecipeAttributes, RecipeQuickStats } from './Attributes'
import { RecipeDescription } from './Description'
import { DetailError } from './DetailError'
import { RecipeEquipment } from './Equipment'
import { RecipeImage } from './Image'
import { RecipeIngredients } from './Ingredients'
import { RecipeNutrition } from './Nutrition'
import { RecipeOptions } from './Options'
import { Title } from './Title'
import { useRecipeDetails } from './hooks/useRecipeDetails'

type DetailProps = {
  coreId: RecipeCoreId
  uuid: RecipeUUID
  title: string
  error: boolean
  loading: boolean
  recipe: MenuRecipe | null
  position?: string
  reload: () => void
  onClose: () => void
  isScrollableBasketEnabled: boolean
}

export const DetailContent = ({
  uuid,
  coreId,
  title,
  error,
  loading,
  recipe: fullRecipe,
  reload,
  onClose,
  position,
  isScrollableBasketEnabled,
}: DetailProps) => {
  const {
    fiveADay,
    dietaryClaims,
    rating,
    prepTime,
    shelfLife,
    kcalPerPortion,
    surcharge,
    images,
  } = useRecipeDetails(uuid, fullRecipe)

  return (
    <>
      <ModalClose onClose={onClose} />
      <ModalHeader>
        <Title data-testing="recipeDetailTitle">{title}</Title>
      </ModalHeader>
      <ModalBody>
        <Box>
          <RecipeImage images={images} title={title} />
          <SectionPanel>
            <RecipeOptions
              recipeUuid={uuid}
              fullRecipeOptions={fullRecipe?.options}
              optionsType={fullRecipe?.options_type}
              ingredientSlots={fullRecipe?.ingredient_slots}
              baseRecipeId={coreId}
            />
            <RecipeQuickStats prepTime={prepTime} kcalPerPortion={kcalPerPortion} rating={rating} />
            <RecipeDescription description={fullRecipe?.description} loading={loading} />
            <RecipeAttributes
              dietaryClaims={dietaryClaims}
              shelfLife={shelfLife}
              fiveADay={fiveADay}
              equipments={fullRecipe?.equipment}
              loading={loading}
            />
          </SectionPanel>
          {error ? (
            <DetailError reload={reload} />
          ) : (
            <>
              <RecipeIngredients
                ingredients={fullRecipe?.ingredients}
                basics={fullRecipe?.basics}
                loading={loading}
              />
              <RecipeEquipment
                equipment={fullRecipe?.equipment}
                onClose={onClose}
                loading={loading}
              />
              <RecipeNutrition
                nutritionalInformation={fullRecipe?.nutritional_information}
                loading={loading}
              />
              <RecipeAllergens
                allergens={fullRecipe?.allergens}
                ingredients={fullRecipe?.ingredients}
                loading={loading}
              />
            </>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <AddRecipeButton
          coreId={coreId}
          id={uuid}
          surcharge={surcharge}
          position={position}
          isScrollableBasketEnabled={isScrollableBasketEnabled}
        />
      </ModalFooter>
    </>
  )
}
